/**
 * Shared Constants for Media Management
 * Used across MediaLibrary, MediaUploadModal, MediaFilters, CategoryDropdown
 */

// Resource Categories - Unified across platform
export const RESOURCE_CATEGORIES = [
  { value: 'General', label: '📦 General', description: 'General resources and documentation' },
  { value: 'Finance', label: '💰 Finance', description: 'Financial resources and guides' },
  { value: 'HR & Workforce', label: '👥 HR & Workforce', description: 'Human resources and workforce management' },
  { value: 'Operations', label: '⚙️ Operations', description: 'Operational resources and procedures' },
  { value: 'Customer Relations', label: '🤝 Customer Relations', description: 'CRM and customer service resources' },
  { value: 'Security', label: '🛡️ Security', description: 'Security and compliance resources' },
  { value: 'Training', label: '🎓 Training', description: 'Training materials and courses' },
  { value: 'Marketing', label: '📢 Marketing', description: 'Marketing materials and campaigns' },
  { value: 'Sales', label: '💼 Sales', description: 'Sales resources and collateral' },
  { value: 'Technical', label: '🔧 Technical', description: 'Technical documentation and specs' },
  { value: 'Overview', label: '🎯 Overview', description: 'Product and platform overviews' }
] as const;

// Extract just the values for dropdowns
export const CATEGORY_VALUES = RESOURCE_CATEGORIES.map(c => c.value);

// Extract labels with icons for display
export const CATEGORY_LABELS = RESOURCE_CATEGORIES.reduce((acc, cat) => {
  acc[cat.value] = cat.label;
  return acc;
}, {} as Record<string, string>);

// Content Types - What kind of file it is
export const CONTENT_TYPES = [
  { value: 'image', label: '🖼️ Image', description: 'Photos, graphics, illustrations' },
  { value: 'video', label: '🎥 Video', description: 'Video files and recordings' },
  { value: 'audio', label: '🎵 Audio', description: 'Audio files and recordings' },
  { value: 'document', label: '📄 Document', description: 'PDFs, Word docs, presentations' },
  { value: 'archive', label: '📦 Archive', description: 'ZIP, RAR, and other archives' },
  { value: 'other', label: '📎 Other', description: 'Other file types' }
] as const;

export const MEDIA_LIBRARY_TYPES = [
  { value: 'image', label: '🖼️ Image', description: 'General image assets', contentTypes: ['image'] },
  { value: 'video', label: '🎥 Video', description: 'Videos and render outputs', contentTypes: ['video'] },
  { value: 'document', label: '📄 Document', description: 'General documents and guides', contentTypes: ['document'] },
  { value: 'screen_shots', label: '🖼️ Screen shots', description: 'Captured workflow screenshots and storyboard frames', contentTypes: ['image'] },
  { value: 'hero_images', label: '🌅 Hero images', description: 'Hero visuals and polished campaign images', contentTypes: ['image'] },
  { value: 'source_images', label: '🧷 Source images', description: 'Reference images and raw visual sources', contentTypes: ['image'] },
  { value: 'module_images', label: '🧩 Module images', description: 'Module-specific product and UI images', contentTypes: ['image'] },
  { value: 'mark_downs', label: '📝 Markdowns', description: 'Markdown content files and drafts', contentTypes: ['document'] },
  { value: 'pdf', label: '📕 PDF', description: 'PDF documents and exports', contentTypes: ['document'] },
  { value: 'audio', label: '🎵 Audio', description: 'Audio files and voice content', contentTypes: ['audio'] },
  { value: 'archive', label: '📦 Archive', description: 'Archive files and bundles', contentTypes: ['archive'] },
  { value: 'other', label: '📎 Other', description: 'Other file types', contentTypes: ['other'] },
] as const;

export type MediaLibraryTypeValue = typeof MEDIA_LIBRARY_TYPES[number]['value'];

export const MEDIA_LIBRARY_TYPE_VALUES = MEDIA_LIBRARY_TYPES.map((type) => type.value);

export const MEDIA_LIBRARY_TYPE_LABELS = MEDIA_LIBRARY_TYPES.reduce((acc, type) => {
  acc[type.value] = type.label;
  return acc;
}, {} as Record<MediaLibraryTypeValue, string>);

export const MEDIA_LIBRARY_TYPE_TAG_PREFIX = 'media_type:';
export const MEDIA_LIBRARY_MODULE_TAG_PREFIX = 'module:';

const SCREENSHOT_KEYWORDS = [
  'screenshot',
  'screen shot',
  'screen-shot',
  'auto-captured',
  'capture',
  'captured',
  'storyboard',
  'training visual',
  'workflow',
  'smart task',
  'smarttask',
  'job detail',
  'appointment detail',
];

const HERO_IMAGE_KEYWORDS = ['hero image', 'hero visual', 'campaign visual', 'launch visual', 'banner visual'];
const SOURCE_IMAGE_KEYWORDS = ['source image', 'reference image', 'reference', 'source visual', 'raw image'];
const MODULE_IMAGE_KEYWORDS = ['module image', 'module visual', 'product image', 'ui image', 'module screenshot'];

export function normalizeMediaLibraryType(value?: string | null): MediaLibraryTypeValue | null {
  if (!value) return null;

  const normalized = `${value}`
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

  return MEDIA_LIBRARY_TYPE_VALUES.includes(normalized as MediaLibraryTypeValue)
    ? normalized as MediaLibraryTypeValue
    : null;
}

export function getMediaLibraryTypeLabel(value?: string | null): string {
  const normalized = normalizeMediaLibraryType(value);
  return normalized ? MEDIA_LIBRARY_TYPE_LABELS[normalized] : '📎 Other';
}

export function getDefaultMediaLibraryTypeFromContentType(
  contentType?: string | null,
  mimeType?: string | null,
  fileName?: string | null,
): MediaLibraryTypeValue {
  const normalizedContentType = `${contentType || ''}`.toLowerCase();
  const normalizedMimeType = `${mimeType || ''}`.toLowerCase();
  const normalizedFileName = `${fileName || ''}`.toLowerCase();

  if (normalizedMimeType.includes('markdown') || normalizedFileName.endsWith('.md') || normalizedFileName.endsWith('.markdown')) {
    return 'mark_downs';
  }

  if (normalizedMimeType.includes('pdf') || normalizedFileName.endsWith('.pdf')) {
    return 'pdf';
  }

  if (normalizedContentType === 'image') return 'image';
  if (normalizedContentType === 'video') return 'video';
  if (normalizedContentType === 'audio') return 'audio';
  if (normalizedContentType === 'document') return 'document';
  if (normalizedContentType === 'archive') return 'archive';
  return 'other';
}

export function buildMediaLibraryTypeTag(mediaType?: string | null): string | null {
  const normalized = normalizeMediaLibraryType(mediaType);
  return normalized ? `${MEDIA_LIBRARY_TYPE_TAG_PREFIX}${normalized}` : null;
}

export function buildMediaLibraryModuleTag(moduleKey?: string | null): string | null {
  const normalized = `${moduleKey || ''}`.trim();
  return normalized ? `${MEDIA_LIBRARY_MODULE_TAG_PREFIX}${normalized}` : null;
}

export function extractMediaLibraryTypeFromTags(tags?: string[] | null): MediaLibraryTypeValue | null {
  if (!Array.isArray(tags)) return null;

  for (const tag of tags) {
    const normalizedTag = `${tag || ''}`.trim().toLowerCase();
    if (normalizedTag.startsWith(MEDIA_LIBRARY_TYPE_TAG_PREFIX)) {
      return normalizeMediaLibraryType(normalizedTag.slice(MEDIA_LIBRARY_TYPE_TAG_PREFIX.length));
    }
  }

  return null;
}

export function extractMediaLibraryModuleFromTags(tags?: string[] | null): string | null {
  if (!Array.isArray(tags)) return null;

  for (const tag of tags) {
    const normalizedTag = `${tag || ''}`.trim();
    if (normalizedTag.toLowerCase().startsWith(MEDIA_LIBRARY_MODULE_TAG_PREFIX)) {
      return normalizedTag.slice(MEDIA_LIBRARY_MODULE_TAG_PREFIX.length).trim() || null;
    }
  }

  return null;
}

export function formatMediaModuleLabel(moduleKey?: string | null): string {
  const normalized = `${moduleKey || ''}`.trim();
  if (!normalized) return 'Unassigned';

  return normalized
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

export function resolveMediaLibraryType(params: {
  explicitType?: string | null;
  tags?: string[] | null;
  metadata?: Record<string, any> | null;
  category?: string | null;
  mimeType?: string | null;
  contentType?: string | null;
  fileName?: string | null;
  title?: string | null;
  description?: string | null;
}): MediaLibraryTypeValue {
  const explicitType = normalizeMediaLibraryType(params.explicitType)
    || normalizeMediaLibraryType(params.metadata?.mediaType)
    || extractMediaLibraryTypeFromTags(params.tags);

  if (explicitType) {
    return explicitType;
  }

  const haystack = [
    params.category,
    params.fileName,
    params.title,
    params.description,
    ...(params.tags || []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (SCREENSHOT_KEYWORDS.some((keyword) => haystack.includes(keyword)) || `${params.category || ''}`.toLowerCase() === 'training-visuals') {
    return 'screen_shots';
  }

  if (HERO_IMAGE_KEYWORDS.some((keyword) => haystack.includes(keyword))) {
    return 'hero_images';
  }

  if (SOURCE_IMAGE_KEYWORDS.some((keyword) => haystack.includes(keyword))) {
    return 'source_images';
  }

  if (MODULE_IMAGE_KEYWORDS.some((keyword) => haystack.includes(keyword))) {
    return 'module_images';
  }

  return getDefaultMediaLibraryTypeFromContentType(params.contentType, params.mimeType, params.fileName);
}

export function isScreenshotMedia(params: Parameters<typeof resolveMediaLibraryType>[0]): boolean {
  return resolveMediaLibraryType(params) === 'screen_shots';
}

export function resolveMediaModuleKey(params: {
  metadata?: Record<string, any> | null;
  tags?: string[] | null;
}): string | null {
  const metadata = params.metadata || null;
  const metadataModuleKey = [
    metadata?.linkedModuleKey,
    metadata?.moduleKey,
    metadata?.module?.key,
    metadata?.smartTaskGuide?.module,
  ].find((value) => typeof value === 'string' && value.trim().length > 0);

  if (typeof metadataModuleKey === 'string' && metadataModuleKey.trim()) {
    return metadataModuleKey.trim();
  }

  return extractMediaLibraryModuleFromTags(params.tags);
}

// Media Purposes - Where/how the media is used
export const MEDIA_PURPOSES = [
  { value: 'generic', label: 'Generic', description: 'General purpose media', icon: '📦' },
  { value: 'ai-content', label: 'AI Content Studio', description: 'Used in AI content generation', icon: '✨' },
  { value: 'video-studio', label: 'Video Studio', description: 'Used in video creation', icon: '🎬' },
  { value: 'content-designer', label: 'Content Designer', description: 'Used in content design', icon: '🎨' },
  { value: 'brochure-designer', label: 'Brochure Designer', description: 'Used in brochure creation', icon: '📰' },
  { value: 'page-builder', label: 'Page Builder', description: 'Used in page building', icon: '🏗️' },
  { value: 'marketing', label: 'Marketing Material', description: 'Finished marketing content', icon: '📢' }
] as const;

// Status Types - Lifecycle stage
export const STATUS_TYPES = [
  { value: 'draft', label: '📝 Draft', description: 'Work in progress, private', color: 'gray' },
  { value: 'published', label: '✓ Published', description: 'Published to client', color: 'green' },
  { value: 'published-resources', label: '🌐 Resources', description: 'Public resources page', color: 'blue' }
] as const;

// MIME Type to Content Type Mapping
export const MIME_TO_CONTENT_TYPE: Record<string, 'image' | 'video' | 'audio' | 'document' | 'archive' | 'other'> = {
  // Images
  'image/jpeg': 'image',
  'image/jpg': 'image',
  'image/png': 'image',
  'image/gif': 'image',
  'image/webp': 'image',
  'image/svg+xml': 'image',
  'image/bmp': 'image',
  'image/ico': 'image',
  'image/tiff': 'image',
  
  // Videos
  'video/mp4': 'video',
  'video/mpeg': 'video',
  'video/webm': 'video',
  'video/quicktime': 'video',
  'video/x-msvideo': 'video',
  'video/x-flv': 'video',
  
  // Audio
  'audio/mpeg': 'audio',
  'audio/mp3': 'audio',
  'audio/wav': 'audio',
  'audio/ogg': 'audio',
  'audio/webm': 'audio',
  'audio/aac': 'audio',
  
  // Documents
  'application/pdf': 'document',
  'application/msword': 'document',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'document',
  'application/vnd.ms-excel': 'document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'document',
  'application/vnd.ms-powerpoint': 'document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'document',
  'text/html': 'document',
  'text/markdown': 'document',
  'text/plain': 'document',
  'text/csv': 'document',
  
  // Archives
  'application/zip': 'archive',
  'application/x-zip-compressed': 'archive',
  'application/x-rar-compressed': 'archive',
  'application/x-7z-compressed': 'archive',
  'application/x-tar': 'archive',
  'application/gzip': 'archive'
};

// Helper function to get content type from MIME type
export function getContentTypeFromMime(mimeType: string): 'image' | 'video' | 'audio' | 'document' | 'archive' | 'other' {
  return MIME_TO_CONTENT_TYPE[mimeType] || 'other';
}

// Helper function to get category label with icon
export function getCategoryLabel(value: string): string {
  return CATEGORY_LABELS[value] || value;
}
